import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Search, Home, MapPin, Loader } from 'lucide-react';
import { API_URL } from '../config/api';
import { formatCurrency } from '../utils/formatters';
import { useToast } from '../context/ToastContext';
import './PropertySearchInput.css';

const PropertySearchInput = ({
    placeholder = 'Search by address...',
    onSelect,
    onClear,
}) => {
    const [query, setQuery] = useState('');
    const [properties, setProperties] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const containerRef = useRef(null);
    const debounceRef = useRef(null);
    const { showToast } = useToast();

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchResults = useCallback(async (q) => {
        if (q.length < 2) {
            setProperties([]);
            setSuggestions([]);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/properties/search-by-address?q=${encodeURIComponent(q)}&limit=8`);
            if (res.ok) {
                const data = await res.json();
                setProperties(data.properties || []);
                setSuggestions(data.suggestions || []);
            }
        } catch {
            setProperties([]);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setShowDropdown(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchResults(val), 300);
    };

    const handleClear = () => {
        setQuery('');
        setProperties([]);
        setSuggestions([]);
        setShowDropdown(false);
        onClear?.();
    };

    const handleSelectProperty = (item) => {
        setQuery(item.address || '');
        setShowDropdown(false);
        onSelect?.(item);
    };

    const handleSelectSuggestion = async (item) => {
        // Nominatim suggestion — use the existing lookup endpoint to find/scrape the property
        setShowDropdown(false);
        setQuery(item.display_name || '');
        setLookupLoading(true);

        try {
            const res = await fetch(`${API_URL}/properties/lookup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address: item.display_name || '',
                    house_number: item.house_number || '',
                    road: item.road || '',
                    city: item.city || '',
                    state: item.state || '',
                    postcode: item.postcode || '',
                }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Property not found');
            }

            const data = await res.json();

            // Now fetch the full property details to populate the preview
            const detailRes = await fetch(`${API_URL}/properties/${data.property_id}`);
            if (detailRes.ok) {
                const detail = await detailRes.json();
                const propertyData = {
                    property_id: data.property_id,
                    address: detail.address,
                    city: detail.city,
                    state: detail.state,
                    beds: detail.beds,
                    baths: detail.baths,
                    sqft: detail.sqft,
                    list_price: detail.list_price || detail.price,
                    primary_photo_url: detail.primary_photo_url,
                };
                setQuery(propertyData.address || item.display_name);
                onSelect?.(propertyData);
            } else {
                // Lookup succeeded but details fetch failed — pass minimal data
                onSelect?.({ property_id: data.property_id, address: item.display_name });
            }
        } catch (err) {
            showToast(err.message || 'Could not find this property', 'error');
        } finally {
            setLookupLoading(false);
        }
    };

    const hasResults = properties.length > 0 || suggestions.length > 0;

    return (
        <div className="prop-search" ref={containerRef}>
            <div className="prop-search-input-wrap">
                <input
                    type="text"
                    className="std-input prop-search-input"
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => { if (query.length >= 2) setShowDropdown(true); }}
                    disabled={lookupLoading}
                />
                {query && !lookupLoading && (
                    <button type="button" className="prop-search-clear" onClick={handleClear}>
                        <X size={14} />
                    </button>
                )}
                <span className="prop-search-icon">
                    {lookupLoading ? <Loader size={16} className="prop-search-spinner" /> : <Search size={16} />}
                </span>
            </div>

            {showDropdown && query.length >= 2 && !lookupLoading && (
                <div className="prop-search-dropdown">
                    {loading && !hasResults && (
                        <div className="prop-search-status">Searching...</div>
                    )}
                    {!loading && !hasResults && (
                        <div className="prop-search-status">No properties found for "{query}"</div>
                    )}

                    {/* DB property matches */}
                    {properties.length > 0 && (
                        <div className="prop-search-group">
                            <div className="prop-search-group-label">Properties</div>
                            {properties.map((item) => (
                                <div
                                    key={item.property_id}
                                    className="prop-search-item"
                                    onClick={() => handleSelectProperty(item)}
                                >
                                    {item.primary_photo_url ? (
                                        <img src={item.primary_photo_url} alt="" className="prop-search-thumb" />
                                    ) : (
                                        <div className="prop-search-thumb prop-search-thumb-empty">
                                            <Home size={16} />
                                        </div>
                                    )}
                                    <div className="prop-search-item-info">
                                        <div className="prop-search-item-address">{item.address}</div>
                                        <div className="prop-search-item-details">
                                            {item.beds || '-'} bd · {item.baths || '-'} ba · {item.sqft ? `${Number(item.sqft).toLocaleString()} sqft` : '-'}
                                            {item.list_price ? ` · ${formatCurrency(item.list_price)}` : ''}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Nominatim address suggestions (only when no DB results) */}
                    {suggestions.length > 0 && (
                        <div className="prop-search-group">
                            <div className="prop-search-group-label">Address Suggestions</div>
                            {suggestions.map((item, i) => (
                                <div
                                    key={`nom-${i}`}
                                    className="prop-search-item"
                                    onClick={() => handleSelectSuggestion(item)}
                                >
                                    <div className="prop-search-thumb prop-search-thumb-empty">
                                        <MapPin size={16} />
                                    </div>
                                    <div className="prop-search-item-info">
                                        <div className="prop-search-item-address">{item.display_name}</div>
                                        <div className="prop-search-item-details">
                                            Click to look up this property
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PropertySearchInput;
